import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import type { HotelPlannerSelection } from '@voll-intelligence/genui-contract';

/** Quantas buscas automáticas uma mesma pergunta ganha antes de o painel
 *  desistir. Duas: a primeira cobre a expiração comum (sessão de ~30min que
 *  venceu enquanto o viajante lia), a segunda cobre a azarada (a sessão nova
 *  nasceu no fim da janela do fornecedor). A terceira já não é expiração, é
 *  indisponibilidade — e insistir custa ~30s de fornecedor por volta. */
const EXPIRED_RETRY_LIMIT = 2;

/**
 * O orçamento de tentativas da pergunta corrente, FORA do componente.
 *
 * Mora no módulo porque na bancada determinística a busca refeita REMONTA a
 * tela: um contador em `useRef` nasceria zerado a cada volta, e um fornecedor
 * que recusasse toda sessão nova manteria o laço de buscas de ~30s para sempre.
 * Uma entrada só — a pergunta em curso —, então trocar o destino ou as datas
 * devolve o orçamento cheio, que é o que o viajante espera ao pedir outra coisa.
 */
let budget: { key: string; used: number } | null = null;

/** A identidade da PERGUNTA, não da sessão nem da resposta: é o que precisa
 *  sobreviver à busca refeita (que muda sessão e handles, e ainda pode
 *  normalizar `placeDetails`) para o orçamento contar as voltas de um mesmo
 *  pedido. */
function criteriaKey(selection: HotelPlannerSelection): string {
  return JSON.stringify([
    selection.place ?? selection.placeDetails?.placeId ?? selection.destinationId ?? '',
    selection.checkin,
    selection.checkout,
    selection.rooms?.length ?? 0,
  ]);
}

function takeRetrySlot(key: string): boolean {
  if (budget?.key !== key) budget = { key, used: 0 };
  if (budget.used >= EXPIRED_RETRY_LIMIT) return false;
  budget.used += 1;
  return true;
}

/** Só para os testes: o orçamento é estado de módulo e vaza entre casos. */
export function resetExpiredSearchRetries(): void {
  budget = null;
}

export type ExpiredSearchRetry = {
  /** Uma busca automática está em voo agora. */
  readonly retrying: boolean;
  /** O orçamento acabou e a lista segue vencida: é indisponibilidade. */
  readonly exhausted: boolean;
};

/**
 * A busca que VENCEU no fornecedor (409) se refaz sozinha, com os mesmos
 * filtros — vale para a conversa e para a bancada, que passam as duas o mesmo
 * `onSearch`.
 *
 * Expirar é decisão do fornecedor sobre uma sessão de ~30min, não uma escolha do
 * viajante: pedir que ele clique "Buscar de novo" é pedir que repita à mão o que
 * a tela já tem em mãos (os critérios estão na faixa de busca, intactos).
 *
 * Sempre pelo caminho de LEITURA (`onSearch`), nunca pelo `submit` da faixa: num
 * card superado o submit vira TURNO — mensagem do viajante na conversa —, e
 * ninguém pediu para falar.
 *
 * Tentativas limitadas (`EXPIRED_RETRY_LIMIT`) e contadas por pergunta, não por
 * montagem. Esgotado o orçamento, quem responde é o painel: a lista não está
 * vencida, está indisponível.
 */
export function useExpiredSearchRetry({
  expired,
  sessionId,
  selection,
  disabled,
  onSearch,
}: {
  readonly expired: boolean;
  /** A sessão que venceu: separa "a mesma expiração de novo" (todo render com a
   *  fase `stale`) de uma expiração NOVA, que merece a sua tentativa. */
  readonly sessionId: string | undefined;
  readonly selection: HotelPlannerSelection | null;
  readonly disabled: boolean;
  readonly onSearch: (selection: HotelPlannerSelection) => Promise<void>;
}): ExpiredSearchRetry {
  const [retrying, setRetrying] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const retriedSession = useRef<string | null>(null);
  const inFlight = useRef(0);
  // Por ref (renovada a cada commit): a seleção chega como objeto novo a cada tecla
  // da faixa, e como dependência ela remontaria o efeito sem que nada sobre a
  // expiração mudasse.
  const selectionRef = useRef(selection);
  useLayoutEffect(() => {
    selectionRef.current = selection;
  });
  useEffect(() => {
    const criteria = selectionRef.current;
    if (!expired || disabled || !criteria) return;
    const session = sessionId ?? '';
    if (retriedSession.current === session) return;
    retriedSession.current = session;
    if (!takeRetrySlot(criteriaKey(criteria))) {
      setExhausted(true);
      return;
    }
    // Contador, e não uma limpeza de efeito: a busca refeita muda a sessão e a
    // fase da página, o que RE-RODA este efeito — e uma limpeza que desarmasse a
    // promessa em voo deixava o painel preso em "buscando de novo" para sempre,
    // porque o `finally` dela já não podia baixar a bandeira.
    inFlight.current += 1;
    setRetrying(true);
    // Pergunta outra (destino, datas) devolve o orçamento — e com ele o direito
    // de esperar em vez de já se declarar indisponível.
    setExhausted(false);
    // Os dois braços do `then`, e não `void ... .finally()`: o `finally` devolve
    // uma promessa que HERDA a rejeição, e o `void` a jogaria fora sem
    // tratamento. A falha da busca refeita não é assunto daqui — quem a conta é
    // a própria tela, pelo aviso que a página seguinte trouxer.
    const settle = () => {
      inFlight.current -= 1;
      if (inFlight.current === 0) setRetrying(false);
    };
    onSearch(criteria).then(settle, settle);
  }, [disabled, expired, onSearch, sessionId]);
  return { retrying, exhausted };
}
